import React from 'react'
import { ComposedChart } from 'recharts'

import { Bar } from '../../../components/chart/Bar'
import { ChartContext } from '../../../components/chart/ChartWrapper'
import { CursorBar } from '../../../components/chart/Cursor'
import { Line } from '../../../components/chart/Line'
import { RedGreenDot } from '../../../components/chart/LineDot'
import { ManualGrid } from '../../../components/chart/ManualGrid'
import { Tooltip } from '../../../components/chart/Tooltip'
import { YAxis } from '../../../components/chart/YAxis'
import { ChartItemColorType } from '../../../components/chart/constants'
import {
  BAR_GAP,
  BAR_WIDTH,
  BAR_WIDTH_NARROW,
  CHART_HEIGHT,
  CHART_MARGINS,
  EXTRA_WIDTH,
  LABELS_MARGIN,
  LABELS_WIDTH,
} from '../constants'
import { EarningReportReaction, EarningValueProperty, EarningsDataType, EarningsEstimateType } from '../types'
import { tickFormatter } from '../utils'
import { PerformanceTooltip } from './PerformanceTooltip'

export type Props = {
  annualData: EarningsDataType[]
  estimateType: EarningsEstimateType
  isCompact: boolean
  reportReactions: EarningReportReaction[]
}

export type ChartYearData = {
  year: string
  isEstimate: boolean
  reportReaction: EarningReportReaction | undefined
} & Record<EarningsDataType['id'], number | null>

export function EarningsChart({ annualData, estimateType, isCompact, reportReactions }: Props) {
  const columnsCount = annualData[0].values.length
  const barWidth = isCompact ? BAR_WIDTH_NARROW : BAR_WIDTH
  const { estimateColumnsCount, hasBarChart, hasLineChart } = React.useMemo(
    () => ({
      estimateColumnsCount: annualData[0].values.filter(({ isEstimate }) => isEstimate).length,
      hasLineChart: annualData.some(({ id, isSelected }) => id === EarningValueProperty.Surprise && isSelected),
      hasBarChart: annualData.some(({ id, isSelected }) => id !== EarningValueProperty.Surprise && isSelected),
    }),
    [annualData]
  )
  const chartData = React.useMemo(() => {
    const data: Record<string, ChartYearData> = {}
    annualData.forEach(({ id, values }) => {
      values.forEach(({ value, year, isEstimate }) => {
        const fiscalPeriod = `20${year.substring(4, 6)}${year.substring(0, 2)}`
        const reportReaction = reportReactions.find((reaction) => reaction.fiscalPeriod === fiscalPeriod)
        data[year] = { ...data[year], year, isEstimate, [id]: value, reportReaction }
      })
    })
    return Object.values(data)
  }, [annualData, reportReactions])

  const chartWidth = columnsCount * barWidth + (columnsCount - 1) * BAR_GAP + EXTRA_WIDTH + LABELS_WIDTH * 1.5
  const estimateAreaWidth = estimateColumnsCount * barWidth + estimateColumnsCount * BAR_GAP
  const barLessChartYMargin = barWidth / 2 + BAR_GAP / 2

  return (
    <div className="flex w-full justify-end">
      <ManualGrid
        visibleLeftAxes={hasLineChart ? 1 : 0}
        estimateAreaWidth={estimateAreaWidth}
        rightOffset={LABELS_WIDTH}
      >
        <ChartContext>
          <ComposedChart
            width={chartWidth}
            height={CHART_HEIGHT}
            data={chartData}
            margin={
              hasBarChart ? CHART_MARGINS : { ...CHART_MARGINS, left: barLessChartYMargin, right: barLessChartYMargin }
            }
            stackOffset="sign" // because we can have negative values
          >
            <YAxis
              yAxisId="left"
              tickFormatter={tickFormatter}
              tickMargin={hasBarChart ? 0 : barLessChartYMargin}
              width={LABELS_WIDTH * 1.5}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={tickFormatter}
              tickMargin={LABELS_MARGIN}
              textAnchor="end"
              width={LABELS_WIDTH}
              interval="preserveStartEnd"
            />
            <Tooltip
              cursor={<CursorBar numberOfDatapoints={chartData.length} />}
              content={<PerformanceTooltip estimateType={estimateType} />}
            />
            {annualData.map(({ id, isSelected, color }) => {
              if (!isSelected) return null

              if (id !== EarningValueProperty.Surprise) {
                return (
                  <Bar
                    key={id}
                    dataKey={id}
                    yAxisId="right"
                    color={() =>
                      [
                        EarningValueProperty.EpsEstimate,
                        EarningValueProperty.EpsReportedEstimate,
                        EarningValueProperty.SalesEstimate,
                      ].includes(id)
                        ? 'gray'
                        : (color as ChartItemColorType)
                    }
                  />
                )
              }

              return (
                <Line
                  key={id}
                  dataKey={id}
                  yAxisId="left"
                  strokeDasharray="3 3"
                  dot={<RedGreenDot />}
                  activeDot={<RedGreenDot isActive />}
                  color={color as ChartItemColorType}
                />
              )
            })}
          </ComposedChart>
        </ChartContext>
      </ManualGrid>
    </div>
  )
}
